import { assert } from "@ember/debug";
import ForcJournalEventBaseComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import retrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import type JournalEventModel from "parog-web/models/journal-event";
import SurveyCompletedJournalEvent from "parog-web/models/survey-completed-journal-event";
import { getLastSurveyCompleted } from "parog-web/utils/journal-events";
import type { ForcBaseArgs } from "./base-component";
import type { Payload } from "./journal-event-based-component";

interface Args extends ForcBaseArgs {
  surveyRequested: JournalEventModel;
}

export default class ForcSurveyBasedComponent extends ForcJournalEventBaseComponent<Args> {
  /***********************
   *   PUBLIC INTERFACE
   ***********************/

  static override journalEventClass = SurveyCompletedJournalEvent;

  /***********************/
  override shouldAutoSaveDraft = true;

  /**
   * We need to override the compute payload because this component
   * do not use the 'journalEventProperties' from the super class.
   * In fact, here the model is the full content of the changeset and it's what we want.
   */
  override computePayload(): Payload {
    // We have to return a copy of the model here because the model is actually
    // rendered and observed at this moment of the life cycle.
    // Here it will work to return directly the model because we don't modify it.
    // But if someone override this method and call super.computePayload() and then try
    // to modify the returned payload without using the 'set' method from '@ember/object'.
    // Then this will break the process which is the saving of the form.
    return Object.assign({}, this.model) as Payload;
  }

  override retrieveModel() {
    assert(
      `You should not override the strategy for the '${this.fullComponentId}' which extends the 'ForcSurveyBasedComponent' without talking about it with the Tech Team and Florian because the 'SurveyCompleted' have a specific behavior. In fact you cant't use a global strategy as you should be aware of the formId.`,
      !this.retrieveJournalEvent()
    );

    const surveyCompletedJournalEvent =
      this.#retrieveSurveyCompletedJournalEvent();

    if (surveyCompletedJournalEvent) {
      return surveyCompletedJournalEvent.rawInputs;
    }
    return {};
  }

  override async saveModel() {
    // If we are in edit mode we first need to archive the old JE
    if (this.isEditMode) {
      await (this.sourceJournalEvent as JournalEventModel).archive();
    }

    const payload = this.computePayload();

    // If it's edition mode then we search for the date in the source journal event.
    // Otherwise we take the one of the payload or we create a new one.
    const occurredAt = this.isEditMode
      ? (this.sourceJournalEvent as JournalEventModel).occurredAt
      : (payload.occurredAt as Date) || new Date();

    const surveyCompleted = this.store.createRecord(
      this.journalEventClassType,
      {
        patient: this.patient,
        occurredAt: occurredAt,
        rawInputs: payload,
        formId: this.fullComponentId,
        // By default, it links the surveyRequested if the form was displayed through a /form-requested route
        surveyRequestedJournalEvent: this.args.surveyRequested
      }
    );

    await surveyCompleted.save();
  }

  #retrieveSurveyCompletedJournalEvent = () => {
    return this.sourceJeId
      ? (retrievalStrategies.sourceJournalEvent.call(
          this
        ) as SurveyCompletedJournalEvent)
      : getLastSurveyCompleted(
          this.patient.surveyCompletedJournalEvents,
          this.fullComponentId
        );
  };
}
